import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, formatINR } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import SEO from "@/components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "@/components/Breadcrumbs";
import { Calendar as CalIcon, User, ArrowLeft, Tag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import SocialShare from "@/components/SocialShare";
import { Button } from "@/components/ui/button";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { settings } = useSettings();
  const siteName = settings.site_name || "HyderabadTourism.in";
  const siteUrl = settings.site_url || (typeof window !== "undefined" ? window.location.origin : "");

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="p-20 text-center text-muted-foreground">Loading...</div>;
  if (notFound || !post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="blog-post-not-found">
        <h1 className="font-display text-3xl mb-2">Article not found</h1>
        <Link to="/blog" className="text-teal underline">Back to blog</Link>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: "Home", to: "/" },
    { label: "Blog", to: "/blog" },
    { label: post.title },
  ];

  return (
    <article className="bg-background" data-testid="blog-post-page">
      <SEO
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt}
        image={post.cover_image}
        type="article"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "description": post.excerpt,
            "image": post.cover_image,
            "datePublished": post.published_at,
            "dateModified": post.updated_at,
            "author": {
              "@type": "Person",
              "name": post.author || `${siteName} Team`,
              "url": typeof window !== "undefined" ? `${window.location.origin}/about` : `${siteUrl}/about`,
            },
            "publisher": {
              "@type": "Organization",
              "name": siteName,
              "logo": { "@type": "ImageObject", "url": settings.logo_url || `${siteUrl}/logo.png` },
            },
            "mainEntityOfPage": typeof window !== "undefined" ? window.location.href : "",
            "keywords": (post.tags || []).join(", "),
          },
          buildBreadcrumbSchema(breadcrumbItems),
        ]}
      />

      {post.cover_image && (
        <div className="relative h-[320px] sm:h-[420px]">
          <img src={post.cover_image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#071827]/85 via-[#071827]/35 to-transparent" />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-8">
            <Link to="/blog" className="inline-flex items-center gap-1 text-white/90 hover:text-white text-sm mb-3 w-fit" data-testid="blog-back">
              <ArrowLeft className="w-4 h-4" /> Back to blog
            </Link>
            <div className="flex gap-2 flex-wrap mb-3">
              {(post.tags || []).map((t) => (
                <Badge key={t} className="bg-white/25 backdrop-blur text-white border-0">{t}</Badge>
              ))}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white font-bold leading-tight max-w-3xl" data-testid="blog-post-title">{post.title}</h1>
            <div className="flex items-center gap-4 text-white/80 text-xs mt-4">
              <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{post.author}</span>
              <span className="flex items-center gap-1"><CalIcon className="w-3.5 h-3.5" />{formatDate(post.published_at)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs items={breadcrumbItems} />
        <div className="flex justify-between items-center mb-6">
          <p className="text-lg text-muted-foreground leading-relaxed">{post.excerpt}</p>
        </div>

        <div className="prose max-w-none prose-headings:font-display prose-headings:text-foreground prose-a:text-teal prose-strong:text-foreground prose-img:rounded-xl" data-testid="blog-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </div>

        <div className="mt-8 pt-6 border-t border-border flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Enjoyed this article?</span>
          <SocialShare title={post.title} />
        </div>

        {post.related_products?.length > 0 && (
          <div className="mt-12">
            <h2 className="font-display text-2xl mb-4">Related packages</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {post.related_products.map((p) => (
                <Link
                  key={p.id}
                  to={`/${p.slug}`}
                  className="flex gap-3 bg-white border border-border rounded-xl p-3 hover:border-teal transition-colors"
                  data-testid={`related-product-${p.slug}`}
                >
                  <img src={p.featured_image} alt="" className="w-24 h-24 rounded-lg object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold line-clamp-1">{p.title}</div>
                    <div className="text-xs text-muted-foreground line-clamp-1">{p.destination}</div>
                    <div className="text-lg font-bold text-teal mt-1">{formatINR(p.adult_price)}</div>
                    <div className="text-xs text-muted-foreground">per person + GST</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link to="/search"><Button className="bg-saffron hover:bg-saffron-dark text-white">Browse all packages</Button></Link>
          <Link to="/blog"><Button variant="outline">More articles</Button></Link>
        </div>
      </div>
    </article>
  );
}
